// implements Filter

import accessibleAutocomplete from "accessible-autocomplete";
import {Filter, FilterProps} from "../Filter";
import {html} from "htm/preact";

interface State {
    selected: any[];
}

interface MultiSelectProps extends FilterProps {
    options: any[];
    optionSelection: any;
    dependsOn?: string;
    optionsEndpoint?: string;
    optionsParameter?: string;
    onChange?: (filter: Filter) => void
}

export class MultiSelect_Typeahead extends Filter<MultiSelectProps, State> {
    options: any[] = []
    autocompleteContainer?: HTMLDivElement

    cleared: any = { selected: [] }

    constructor(props: any) {
        super(props);

        this.clear = this.clear.bind(this);
        this.options = props.options.sort((a: any, b: any) => a.name.localeCompare(b.name));

        if(this.state.value == null){
            this.setStateValue({selected: []})
        }
        else{
            let newStateSelected: any[] = []
            this.state.value.selected.forEach((value: any) => {
                newStateSelected.push({ name: this.getNameForValue(value), value: value})
            });

            this.initialStateValue = {selected: newStateSelected};
            this.setStateValue({selected: newStateSelected})
        }
    }

    render() {
        return html `
            <div class="content">
                <div class="form-group">
                    <label class="govuk-label govuk-visually-hidden" for="${this.id}-autocomplete">${this.name}</label>
                    <ul class="govuk-list multiselect-chosen-options">
                        ${this.state.value.selected.map((option: any) => html `
                            <li class="delta-filters_chosen-option">
                                ${option.name}
                                <button type="button" class="govuk-link govuk-link--no-visited-state delta-filters_remove-option" data-value="${option.value}" aria-label="Remove ${option.name}" onClick=${this.removeOption.bind(this)}></button>
                            </li>
                        `)}
                    </ul>
                    <div class="multiselect-input-container" ref=${(el: HTMLDivElement) => this.autocompleteContainer = el}></div>
                </div>
            </div>
        `
    }

    componentDidMount() {
        super.componentDidMount();

        if (!this.autocompleteContainer) {
            return;
        }

        accessibleAutocomplete({
            element: this.autocompleteContainer,
            id: `${this.id}-autocomplete`,
            source: (query: string, populateResults: (results: string[]) => void) => {
                const selectedValues = new Set(this.state.value.selected.map((option: any) => option.value));
                const normalisedQuery = query.toLowerCase();
                populateResults(this.options
                    .filter((option: any) => !selectedValues.has(option.value) && option.name.toLowerCase().includes(normalisedQuery))
                    .map((option: any) => option.name));
            },
            autoselect: true,
            confirmOnBlur: false,
            displayMenu: "overlay",
            minLength: 0,
            showAllValues: true,
            onConfirm: (confirmedName: string | null) => this.confirmOption(confirmedName)
        });
    }

    private confirmOption(confirmedName: string | null) {
        const option = this.options.find((candidate: any) => candidate.name === confirmedName);
        if (!option || this.state.value.selected.some((selected: any) => selected.value === option.value)) {
            return;
        }

        this.addOptionToState({name: option.name, value: option.value});
        window.setTimeout(() => this.clearInput());
    }

    private clearInput() {
        const input = document.getElementById(`${this.id}-autocomplete`) as HTMLInputElement | null;
        if (input) {
            input.value = "";
            input.dispatchEvent(new Event("input", {bubbles: true}));
        }
    }

    removeOption(e: Event) {
        const optionValue = (e.currentTarget as HTMLElement).getAttribute("data-value");
        if (optionValue) {
            this.removeOptionFromStateByValue(optionValue);
        }
    }

    getNameForValue(value: string){
        return this.options.find((option: any) => option.value == value)?.name;
    }

    clear(cb?: () => void, fullClear?: boolean){
        this.clearInput();
        super.clear(cb, fullClear);
    }

    getQueryParam(): Record<string, string> {
        if(this.state.value && this.state.value?.selected?.length > 0){
            let selectedValues = this.state.value.selected.map((s: any) => s.value);
            let params: { [key: string]: string } = {};

            for(let i = 0; i < selectedValues.length; i++){
                params[this.id + "[" + i + "]"] = selectedValues[i];
            }

            return params;
        }
        return {};
    }

    async loadDependentOptions(parentSelections: any[]) {
        const selectedValue = parentSelections.length > 0 ? parentSelections[0].value : null;
        this.options = [];
        this.setStateValue({selected: []});

        const props = this.props as MultiSelectProps;
        if (selectedValue && props.optionsEndpoint) {
            const parameter = props.optionsParameter || props.dependsOn || "value";
            const response = await fetch(`${props.optionsEndpoint}?${encodeURIComponent(parameter)}=${encodeURIComponent(selectedValue)}`);
            if (response.ok) {
                const values = await response.json();
                this.options = values.map((value: any) => typeof value === "string" ? {name: value, value} : value);
            }
        }

        this.forceUpdate();
    }
}
